import React, { useState, useRef, useCallback, useEffect } from 'react';
import styles from './completeModal.module.scss';
import Modal from '@src/components/modal/Modal';
import Button from '@src/components/button/Button';
import Spinner from '@src/components/spinner/Spinner';
import artworkImage from './artwork.png';
import { logger } from '../../../utils/logger';

type Props = {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  /**
   * Called on Mark Complete / countdown finish.
   * `exitPresentation` is captured when this modal instance mounts (End Lesson vs in-lesson auto).
   */
  onComplete: (exitPresentation: boolean) => void;
  /** When false, countdown does not auto-call onComplete (End Lesson → manual Mark Complete). */
  enableCountdownAutoComplete?: boolean;
  /**
   * Captured once at mount. When true, Mark Complete must leave presentation.
   * Remount via `key` when opening from End Lesson so this stays accurate.
   * Also latched if the prop flips to true while mounted.
   */
  exitPresentationOnComplete?: boolean;
};

function AutoCompleteModal(props: Props) {
  const {
    open,
    loading,
    onClose,
    onComplete,
    enableCountdownAutoComplete = true,
    exitPresentationOnComplete = false,
  } = props;
  const [percent, setPercent] = useState<number>(0);
  const countDownTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const completedRef = useRef(false);
  // Mount-only base; latched true if End Lesson intent arrives later.
  const exitPresentationRef = useRef(!!exitPresentationOnComplete);
  const onCompleteRef = useRef(onComplete);
  const enableCountdownRef = useRef(enableCountdownAutoComplete);
  onCompleteRef.current = onComplete;
  enableCountdownRef.current = enableCountdownAutoComplete;

  useEffect(() => {
    if (exitPresentationOnComplete) {
      exitPresentationRef.current = true;
    }
  }, [exitPresentationOnComplete]);

  const clearTimer = useCallback(() => {
    if (countDownTimer.current) {
      clearInterval(countDownTimer.current);
      countDownTimer.current = null;
    }
  }, []);

  const finishComplete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    clearTimer();
    const exitPresentation = exitPresentationRef.current;
    logger.info('[OP-complete] AutoCompleteModal finishComplete', {
      exitPresentation,
      exitPresentationOnComplete,
    });
    onCompleteRef.current(exitPresentation);
  }, [clearTimer, exitPresentationOnComplete]);

  useEffect(() => {
    countDownTimer.current = setInterval(() => {
      setPercent((p) => (p >= 99 ? p : p + 1));
    }, 5000 / 100);

    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  // Fire completion outside the setState updater — calling parent setState inside
  // setPercent's updater caused "Cannot update CourseContentProvider while rendering AutoCompleteModal".
  useEffect(() => {
    if (percent < 99) return;
    clearTimer();
    if (enableCountdownRef.current) {
      finishComplete();
    }
  }, [percent, clearTimer, finishComplete]);

  const handleCancel = useCallback(() => {
    clearTimer();
    onClose();
  }, [clearTimer, onClose]);

  const handleComplete = useCallback(() => {
    finishComplete();
  }, [finishComplete]);

  return (
    <Modal global={false} open={open} onClose={handleCancel} className={styles.autoWrapper} closeButton={false}>
      <div className={styles.opContent}>
        <div className={styles.artworkView}>
          <img src={artworkImage} alt='art' />
        </div>
        <div className={styles.opCongrateView}><h2>Congratulations!</h2><span>{'You have successfully completed this Lesson.'}</span></div>
        <div className={styles.notifyDetailView}>
          <div className={styles.notifyDetailTitle}>
          This lesson will close in... <span>{Math.ceil(5 - 5.0 / 100 * (percent || 0))}</span> second
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <Button size={'small'} disabled={loading} outline={true}  onClick={handleCancel} className={styles.cancelButton} label={'Cancel'} />
          <div onClick={loading ? () =>{} : handleComplete} className={styles.completeButton} style={{cursor: loading ? 'not-allowed' : 'pointer'}}>
            <div className={styles.progressBar} style={{width: percent < 99 ? `${percent}%` : '', right: percent >= 99 ? '-1px' : '' }}></div>
            <div className={styles.buttonText}>{loading ? <div style={{width: 40, marginRight: 10}}><Spinner width={40}/></div> : undefined}<span>Mark Lesson Completed</span></div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default AutoCompleteModal;
